/**
 * UNIFIED ANALYTICS SYSTEM - Robust Architecture v2
 * 
 * Solves:
 * 1. IP/Geo missing in web_requests - now waits for resolution
 * 2. Multiple session_start events - uses localStorage flag
 * 3. Real-time panel not showing active sessions - proper initialization
 * 
 * Tables used:
 * - web_requests: Page views + tool executions (for analytics)
 * - user_sessions: Session tracking (for real-time monitoring)
 * - session_events: Detailed event log (for behavior analysis)
 */

import { useEffect, useRef, useCallback, createContext, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// ============================================================================
// CONSTANTS
// ============================================================================

const SESSION_KEY = 'lowkeyx_session_id';
const SESSION_INIT_KEY = 'lowkeyx_session_initialized';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 min inactivity = new session
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 seconds
const PAGE_VIEW_DEBOUNCE_MS = 1000;

// ============================================================================
// GLOBAL STATE (persists across hot reloads)
// ============================================================================

let globalIP: string | null = null;
let globalGeo: { country: string; city: string } | null = null;
let ipFetchPromise: Promise<string> | null = null;
let geoFetchPromise: Promise<{ country: string; city: string }> | null = null;
let dataReady = false;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get or create a stable session ID that persists across page refreshes
 * but expires after 30 minutes of inactivity
 */
const getOrCreateSessionId = (): string => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    const timestampKey = 'lowkeyx_session_ts';
    const timestamp = sessionStorage.getItem(timestampKey);
    const now = Date.now();
    
    if (stored && timestamp) {
      const elapsed = now - parseInt(timestamp, 10);
      if (elapsed < SESSION_DURATION_MS) {
        // Update timestamp to extend session
        sessionStorage.setItem(timestampKey, now.toString());
        return stored;
      }
    }
    
    // Create new session
    const newId = `sess_${now}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(SESSION_KEY, newId);
    sessionStorage.setItem(timestampKey, now.toString());
    
    // Clear initialization flag for new session
    sessionStorage.removeItem(SESSION_INIT_KEY);
    
    return newId;
  } catch {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
};

/**
 * Check if this session was already initialized
 */
const isSessionInitialized = (): boolean => {
  try {
    const currentSession = sessionStorage.getItem(SESSION_KEY);
    const initSession = sessionStorage.getItem(SESSION_INIT_KEY);
    return currentSession === initSession;
  } catch {
    return false;
  }
};

/**
 * Mark session as initialized
 */
const markSessionInitialized = () => {
  try {
    const currentSession = sessionStorage.getItem(SESSION_KEY);
    if (currentSession) {
      sessionStorage.setItem(SESSION_INIT_KEY, currentSession);
    }
  } catch {}
};

/**
 * Get device and browser information
 */
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  let deviceType: 'mobile' | 'tablet' | 'desktop' | 'bot' = 'desktop';
  const isBot = /bot|crawler|spider|scraper|curl|wget|python|java|go-http/i.test(ua);
  if (isBot) {
    deviceType = 'bot';
  } else if (/Mobile|Android|iPhone|iPod/.test(ua) && !/iPad|Tablet/.test(ua)) {
    deviceType = 'mobile';
  } else if (/iPad|Tablet|PlayBook/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) {
    deviceType = 'tablet';
  }

  let browser = 'Unknown';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR/')) browser = 'Opera';

  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return { deviceType, browser, os, userAgent: ua, isBot };
};

/**
 * Validate & normalize IPs (IPv4 + IPv6)
 */
const normalizeIP = (raw: string | null | undefined): string => {
  const v = (raw || '').trim();
  if (!v) return 'unknown';
  // If x-forwarded-for style list
  const first = v.split(',')[0]?.trim();
  // Strip IPv4 port ("1.2.3.4:1234")
  if (first.includes('.') && first.includes(':')) return first.split(':')[0];
  return first;
};

const isValidIP = (ip: string): boolean => {
  if (!ip || ip === 'unknown') return false;
  // IPv4
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;
  if (ipv4Regex.test(ip)) return true;
  // IPv6 (covers compressed forms reasonably well)
  const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;
  return ipv6Regex.test(ip);
};

/**
 * Fetch client IP with caching and fallback.
 * IMPORTANT: Prefer backend function because it sees the real client IP even when the browser is behind proxies/privacy limits.
 */
const getClientIP = async (): Promise<string> => {
  if (globalIP && globalIP !== 'unknown' && isValidIP(globalIP)) return globalIP;
  if (ipFetchPromise) return ipFetchPromise;

  ipFetchPromise = (async () => {
    // 1) Backend function (best)
    try {
      const { data, error } = await supabase.functions.invoke('get-client-ip');
      if (!error && data?.ip) {
        const ip = normalizeIP(data.ip);
        if (isValidIP(ip)) {
          globalIP = ip;
          return ip;
        }
      }
    } catch {
      // Ignore and fallback
    }

    // 2) Browser public services (fallback)
    const services = [
      { url: 'https://api.ipify.org?format=json', extract: (d: any) => d.ip },
      { url: 'https://api.my-ip.io/v2/ip.json', extract: (d: any) => d.ip },
      { url: 'https://ipinfo.io/json', extract: (d: any) => d.ip },
    ];

    for (const service of services) {
      try {
        const response = await fetch(service.url, { signal: AbortSignal.timeout(3000) });
        if (!response.ok) continue;
        const data = await response.json();
        const ip = normalizeIP(service.extract(data));
        if (isValidIP(ip)) {
          globalIP = ip;
          return ip;
        }
      } catch {
        // Try next service
      }
    }

    globalIP = 'unknown';
    return 'unknown';
  })();

  return ipFetchPromise;
};

/**
 * Get geolocation from IP with caching
 */
const getGeoInfo = async (ip: string): Promise<{ country: string; city: string }> => {
  if (globalGeo) return globalGeo;
  if (!ip || ip === 'unknown') {
    globalGeo = { country: 'Unknown', city: 'Unknown' };
    return globalGeo;
  }
  if (geoFetchPromise) return geoFetchPromise;
  
  geoFetchPromise = (async () => {
    const services = [
      { 
        url: `https://ipapi.co/${ip}/json/`, 
        extract: (d: any) => ({ country: d.country_name, city: d.city })
      },
      { 
        url: `https://ip-api.com/json/${ip}`, 
        extract: (d: any) => ({ country: d.country, city: d.city })
      }
    ];
    
    for (const service of services) {
      try {
        const response = await fetch(service.url, {
          signal: AbortSignal.timeout(4000)
        });
        if (response.ok) {
          const data = await response.json();
          if (!data.error && data.country_name !== undefined || data.country !== undefined) {
            const extracted = service.extract(data);
            if (extracted.country) {
              globalGeo = {
                country: extracted.country || 'Unknown',
                city: extracted.city || 'Unknown'
              };
              return globalGeo;
            }
          }
        }
      } catch {
        // Try next service
      }
    }
    
    globalGeo = { country: 'Unknown', city: 'Unknown' };
    return globalGeo;
  })();
  
  return geoFetchPromise;
};

/**
 * Ensure IP and Geo data are ready before any tracking
 */
const ensureDataReady = async (): Promise<boolean> => {
  if (dataReady && globalIP && globalGeo) return true;
  
  try {
    const ip = await getClientIP();
    await getGeoInfo(ip);
    dataReady = true;
    return true;
  } catch {
    dataReady = true; // Mark as ready even on failure to prevent blocking
    return true;
  }
};

// ============================================================================
// ANALYTICS CONTEXT
// ============================================================================

interface AnalyticsContextType {
  sessionId: string;
  trackToolExecution: (toolName: string, query: string, success: boolean, latencyMs?: number) => void;
  trackError: (error: string, context?: Record<string, unknown>) => void;
  trackAction: (actionName: string, data?: Record<string, unknown>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    return {
      sessionId: '',
      trackToolExecution: () => {},
      trackError: () => {},
      trackAction: () => {}
    };
  }
  return context;
};

// ============================================================================
// ANALYTICS PROVIDER
// ============================================================================

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, isAdmin, loading: authLoading } = useAuth();
  const location = useLocation();
  
  const sessionId = useRef<string>(getOrCreateSessionId());
  const lastTrackedPath = useRef<string>('');
  const lastTrackTime = useRef<number>(0);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const authTrackedRef = useRef<boolean>(false);
  const initializingRef = useRef<boolean>(false);
  const readyRef = useRef<boolean>(false);
  
  const [isAdminUser, setIsAdminUser] = useState<boolean | null>(null);
  const [authResolved, setAuthResolved] = useState<boolean>(false);

  // Wait for auth to fully resolve before any tracking
  useEffect(() => {
    if (!authLoading) {
      setIsAdminUser(isAdmin);
      setAuthResolved(true);
    }
  }, [isAdmin, authLoading]);

  // Skip tracking if: auth not resolved, or user is admin
  const shouldSkip = !authResolved || isAdminUser === true;

  // ============================================================================
  // SESSION INITIALIZATION - Runs ONCE per session
  // ============================================================================
  
  const initializeSession = useCallback(async () => {
    // Multiple guards to prevent duplicate initialization
    if (shouldSkip) return;
    if (initializingRef.current) return;
    if (isSessionInitialized()) {
      readyRef.current = true;
      return;
    }
    
    initializingRef.current = true;

    try {
      // CRITICAL: Wait for IP and Geo before any tracking
      await ensureDataReady();
      
      const device = getDeviceInfo();
      const urlParams = new URLSearchParams(window.location.search);

      // Create session in user_sessions
      await supabase.from('user_sessions').upsert({
        session_id: sessionId.current,
        user_id: user?.id || null,
        user_email: user?.email || profile?.email || null,
        ip_address: globalIP !== 'unknown' ? globalIP : null,
        user_agent: device.userAgent,
        country: globalGeo?.country !== 'Unknown' ? globalGeo?.country : null,
        city: globalGeo?.city !== 'Unknown' ? globalGeo?.city : null,
        is_authenticated: !!user,
        is_active: true,
        started_at: new Date().toISOString(),
        last_activity_at: new Date().toISOString(),
        pages_visited: 1,
        total_events: 1,
        device_type: device.deviceType,
        browser: device.browser,
        os: device.os,
        referrer: document.referrer || null,
        utm_source: urlParams.get('utm_source') || null,
        utm_medium: urlParams.get('utm_medium') || null,
        utm_campaign: urlParams.get('utm_campaign') || null,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight
      }, { onConflict: 'session_id' });

      // Track session start event (ONCE)
      await supabase.from('session_events').insert({
        session_id: sessionId.current,
        event_type: 'session_start',
        // CRITICAL: include IP here so the realtime panel can reconstruct sessions even
        // when the user_sessions row couldn't be created (privacy/mobile failures).
        event_data: {
          ip: globalIP && globalIP !== 'unknown' ? globalIP : null,
          device: device.deviceType,
          browser: device.browser,
          os: device.os,
          country: globalGeo?.country,
          city: globalGeo?.city,
        },
        page_path: location.pathname,
        page_title: document.title,
      });

      // Save IP history for authenticated users
      if (user?.id && globalIP && globalIP !== 'unknown') {
        const locationStr = globalGeo?.city !== 'Unknown' 
          ? `${globalGeo?.city}, ${globalGeo?.country}` 
          : globalGeo?.country;
        try {
          await supabase.rpc('upsert_user_ip', {
            _user_id: user.id,
            _ip_address: globalIP,
            _location: locationStr !== 'Unknown' ? locationStr : null
          });
        } catch {}
      }

      // Mark as initialized AFTER success
      markSessionInitialized();
      readyRef.current = true;
      
    } catch (err) {
      console.debug('Analytics init error:', err);
      readyRef.current = true; // Still mark ready to unblock page views
    } finally {
      initializingRef.current = false;
    }
  }, [shouldSkip, user, profile, location.pathname]);

  // ============================================================================
  // SESSION ENSURANCE - Guarantee a visible session row even if init failed
  // ============================================================================

  const ensureSessionRowExists = useCallback(async () => {
    if (shouldSkip) return;

    try {
      const nowIso = new Date().toISOString();

      // Insert-only upsert: if the session row is missing, create it.
      // If it already exists, do nothing (preserves started_at, geo, etc.).
      await supabase
        .from('user_sessions')
        .upsert(
          {
            session_id: sessionId.current,
            user_id: user?.id || null,
            user_email: user?.email || profile?.email || null,
            is_authenticated: !!user,
            is_active: true,
            started_at: nowIso,
            last_activity_at: nowIso,
            pages_visited: 0,
            total_events: 0,
          },
          {
            onConflict: 'session_id',
            ignoreDuplicates: true,
          }
        );
    } catch {
      // Silent fail: we still allow event tracking to continue.
    }
  }, [shouldSkip, user, profile]);

  // ============================================================================
  // HEARTBEAT - Keep session alive (lightweight)
  // ============================================================================

  const sendHeartbeat = useCallback(async () => {
    if (shouldSkip || !readyRef.current) return;

    // If initialization failed (common on some devices / privacy settings),
    // ensure the session entity exists so it appears in the Live panel.
    await ensureSessionRowExists();

    try {
      await supabase
        .from('user_sessions')
        .update({
          last_activity_at: new Date().toISOString(),
          is_active: true,
          is_authenticated: !!user,
          user_id: user?.id || null,
          user_email: user?.email || profile?.email || null,
        })
        .eq('session_id', sessionId.current);
    } catch {}
  }, [shouldSkip, user, profile, ensureSessionRowExists]);

  // ============================================================================
  // PAGE VIEW TRACKING
  // ============================================================================

  const trackPageView = useCallback(async (path: string) => {
    if (shouldSkip) return;
    if (!readyRef.current) return; // Wait for initialization
    
    const now = Date.now();
    
    // Debounce same path within 1 second
    if (path === lastTrackedPath.current && now - lastTrackTime.current < PAGE_VIEW_DEBOUNCE_MS) {
      return;
    }
    
    lastTrackedPath.current = path;
    lastTrackTime.current = now;

    try {
      const device = getDeviceInfo();

      // Ensure the session row exists even if initial upsert failed.
      // This is what makes sessions appear as entities in the Live panel.
      await ensureSessionRowExists();
      
      // Track in web_requests WITH IP and Geo
      await supabase.from('web_requests').insert({
        request_id: `req_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
        session_id: sessionId.current,
        user_id: user?.id || null,
        method: 'GET',
        path: path,
        status_code: 200,
        ip_address: globalIP !== 'unknown' ? globalIP : null,
        user_agent: device.userAgent,
        referer: document.referrer || null,
        country: globalGeo?.country !== 'Unknown' ? globalGeo?.country : null,
        city: globalGeo?.city !== 'Unknown' ? globalGeo?.city : null,
        device_type: device.deviceType,
        browser: device.browser,
        os: device.os,
        is_authenticated: !!user,
        request_type: path.startsWith('/tools') ? 'tool' : 'page',
        risk_level: 'normal',
        is_bot: device.isBot
      });

      // Track in session_events for real-time panel
      await supabase.from('session_events').insert({
        session_id: sessionId.current,
        event_type: 'page_view',
        event_data: { path, referrer: document.referrer },
        page_path: path,
        page_title: document.title
      });

      // Update session activity
      await supabase
        .from('user_sessions')
        .update({ 
          last_activity_at: new Date().toISOString(),
          is_active: true
        })
        .eq('session_id', sessionId.current);
        
    } catch (err) {
      console.debug('Page view tracking error:', err);
    }
  }, [shouldSkip, user, ensureSessionRowExists]);

  // ============================================================================
  // EVENT TRACKING METHODS
  // ============================================================================

  const trackToolExecution = useCallback((
    toolName: string, 
    query: string, 
    success: boolean, 
    latencyMs?: number
  ) => {
    if (shouldSkip || !readyRef.current) return;

    (async () => {
      try {
        const device = getDeviceInfo();

        await ensureSessionRowExists();
        
        // Track in session_events
        await supabase.from('session_events').insert({
          session_id: sessionId.current,
          event_type: 'tool_use',
          event_data: { tool: toolName, query, success, latency_ms: latencyMs },
          page_path: location.pathname,
          page_title: document.title,
          duration_ms: latencyMs
        });

        // Track in web_requests for tool analytics WITH IP
        await supabase.from('web_requests').insert({
          request_id: `tool_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
          session_id: sessionId.current,
          user_id: user?.id || null,
          method: 'POST',
          path: `/tools/${toolName}`,
          status_code: success ? 200 : 500,
          latency_ms: latencyMs,
          request_type: 'tool',
          tool_name: toolName,
          is_authenticated: !!user,
          ip_address: globalIP !== 'unknown' ? globalIP : null,
          country: globalGeo?.country !== 'Unknown' ? globalGeo?.country : null,
          city: globalGeo?.city !== 'Unknown' ? globalGeo?.city : null,
          device_type: device.deviceType,
          browser: device.browser,
          os: device.os,
          metadata: { query, success }
        } as any);
      } catch {}
    })();
  }, [shouldSkip, user, location.pathname, ensureSessionRowExists]);

  const trackError = useCallback((error: string, context?: Record<string, unknown>) => {
    if (shouldSkip || !readyRef.current) return;

    (async () => {
      try {
        await ensureSessionRowExists();

        await supabase.from('session_events').insert({
          session_id: sessionId.current,
          event_type: 'error',
          event_data: { error, ...context },
          page_path: location.pathname,
          page_title: document.title
        });
      } catch {}
    })();
  }, [shouldSkip, location.pathname, ensureSessionRowExists]);

  const trackAction = useCallback((actionName: string, data?: Record<string, unknown>) => {
    if (shouldSkip || !readyRef.current) return;

    (async () => {
      try {
        await ensureSessionRowExists();

        await supabase.from('session_events').insert({
          session_id: sessionId.current,
          event_type: 'action',
          event_data: { action: actionName, ...data },
          page_path: location.pathname,
          page_title: document.title
        });
        
        // Update session activity
        await supabase
          .from('user_sessions')
          .update({ 
            last_activity_at: new Date().toISOString(),
            is_active: true
          })
          .eq('session_id', sessionId.current);
      } catch {}
    })();
  }, [shouldSkip, location.pathname, ensureSessionRowExists]);

  // ============================================================================
  // LIFECYCLE EFFECTS
  // ============================================================================

  // Initialize session on mount
  useEffect(() => {
    if (isAdminUser === null) return; // Wait for auth to load
    if (isAdminUser === true) return; // Skip for admins
    
    initializeSession();

    // Start heartbeat
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Handle page visibility
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };

    // Handle page unload - mark session as inactive
    const handleUnload = () => {
      try {
        navigator.sendBeacon?.(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_sessions?session_id=eq.${sessionId.current}`,
          JSON.stringify({ is_active: false, ended_at: new Date().toISOString() })
        );
      } catch {}
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [isAdminUser, initializeSession, sendHeartbeat]);

  // Track page views on route change
  useEffect(() => {
    if (isAdminUser === null) return;
    if (isAdminUser === true) return;
    if (!readyRef.current) {
      // Wait a bit for initialization to complete
      const timeout = setTimeout(() => {
        if (readyRef.current) {
          trackPageView(location.pathname);
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
    
    trackPageView(location.pathname);
  }, [location.pathname, isAdminUser, trackPageView]);

  // Handle authentication transition (anonymous → authenticated)
  useEffect(() => {
    if (shouldSkip) return;
    if (!user?.id) return;
    if (!readyRef.current) return;
    if (authTrackedRef.current) return;
    
    authTrackedRef.current = true;

    (async () => {
      try {
        // Update session with user info
        await supabase
          .from('user_sessions')
          .update({
            user_id: user.id,
            user_email: user.email || profile?.email || null,
            is_authenticated: true,
            last_activity_at: new Date().toISOString()
          })
          .eq('session_id', sessionId.current);

        // Track authentication event ONCE
        await supabase.from('session_events').insert({
          session_id: sessionId.current,
          event_type: 'user_authenticated',
          event_data: { email: user.email || profile?.email },
          page_path: location.pathname
        });
      } catch {}
    })();
  }, [user?.id, shouldSkip, profile?.email, location.pathname]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <AnalyticsContext.Provider value={{
      sessionId: sessionId.current,
      trackToolExecution,
      trackError,
      trackAction
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default useAnalytics;
